"use client";
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCostCodeMap } from "./useCostCodeMap";
import SubAgreementsPanel from "./SubAgreementsPanel";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const WO_TYPE_LABELS = {
  piecework: "Piecework",
  hourly: "Hourly",
  union_piecework: "Union Piecework",
  union_hourly: "Union Hourly",
  subcontractor: "Subcontractor",
};
const WO_TYPE_COLORS = {
  piecework: "bg-orange-700/60 text-orange-200",
  hourly: "bg-blue-700/60 text-blue-200",
  union_piecework: "bg-teal-700/60 text-teal-200",
  union_hourly: "bg-cyan-700/60 text-cyan-200",
  subcontractor: "bg-violet-700/60 text-violet-200",
};

// ── Summary card ──────────────────────────────────────────────────────────────
function SummaryCard({
  label,
  value,
  color = "text-white",
  sub,
  sub2,
  flag,
  isQty,
}) {
  return (
    <div
      className={cls(
        "rounded-xl border p-4",
        flag === "red"
          ? "bg-red-950 border-red-700"
          : flag === "green"
            ? "bg-green-950 border-green-700"
            : flag === "yellow"
              ? "bg-yellow-950 border-yellow-700"
              : "bg-gray-800 border-gray-700",
      )}
    >
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={cls("text-xl font-bold font-mono", color)}>
        {isQty ? fmtQty(value) : `$${fmt(value)}`}
      </p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      {sub2 && <p className="text-xs text-yellow-500 mt-0.5">{sub2}</p>}
    </div>
  );
}

// ── Progress bar — yellow underlay = pending, orange/green fill = installed ──
function QtyBar({ installed, pending, netQty }) {
  const target = netQty > 0 ? netQty : installed + pending;
  const pct = target > 0 ? Math.min(100, (installed / target) * 100) : 0;
  const pendPct =
    target > 0 ? Math.min(100, ((installed + pending) / target) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="relative flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-yellow-600/40 rounded-full"
          style={{ width: `${pendPct}%` }}
        />
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full transition-all",
            pct >= 100 ? "bg-green-500" : "bg-orange-500",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-gray-400 shrink-0 font-mono whitespace-nowrap">
        {fmtQty(installed)}/{fmtQty(target)}
      </span>
    </div>
  );
}

// ── Expanded detail panel — CO breakdown + PW log history ────────────────────
function ExpandedDetailPanel({ row, logHistory, colSpan }) {
  const coRows = row._coRows || [];
  const logs = logHistory[row._id?.toString()] || [];
  const approved = logs.filter((l) => l.status === "approved");
  const pending = logs.filter((l) => l.status === "pending");

  return (
    <tr className="bg-gray-950/60">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="ml-6 space-y-3">
          {/* CO breakdown */}
          {coRows.length > 0 && (
            <div className="rounded-lg border border-purple-800/40 overflow-hidden">
              <div className="bg-purple-950/40 px-3 py-1.5 border-b border-purple-800/30 flex items-center gap-2">
                <span className="text-[10px] font-semibold text-purple-300 uppercase tracking-wide">
                  Change Order Details
                </span>
                <span className="text-[10px] text-purple-600">
                  — {coRows.length} CO{coRows.length !== 1 ? "s" : ""}
                </span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-purple-950/20">
                  <tr>
                    {[
                      "CO Ref",
                      "Bid Area",
                      "Section",
                      "Δ Qty",
                      "Unit Rate",
                      "Δ Amount",
                      "UOM",
                    ].map((h) => (
                      <th
                        key={h}
                        className={cls(
                          "px-3 py-1.5 text-purple-400/70 font-medium",
                          ["Δ Qty", "Unit Rate", "Δ Amount"].includes(h)
                            ? "text-right"
                            : "text-left",
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-900/30">
                  {coRows.map((co, i) => {
                    const dQty = co.deltaQty || co.quantity || 0;
                    return (
                      <tr
                        key={i}
                        className={
                          i % 2 === 0 ? "bg-purple-950/10" : "bg-purple-950/5"
                        }
                      >
                        <td className="px-3 py-2 font-mono text-purple-300 font-semibold">
                          {co.changeOrderRef || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          {co.bidArea || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-400 font-mono">
                          {co.sectionNumber || "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">
                          <span
                            className={
                              dQty >= 0 ? "text-green-400" : "text-red-400"
                            }
                          >
                            {dQty >= 0 ? "+" : ""}
                            {fmtQty(dQty)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-300 font-mono">
                          ${fmt(co.unitRate || 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">
                          <span
                            className={
                              (co.amount || 0) >= 0
                                ? "text-purple-300"
                                : "text-red-400"
                            }
                          >
                            {(co.amount || 0) >= 0 ? "+" : ""}$
                            {fmt(co.amount || 0)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          {co.uom || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {coRows.length > 1 && (
                  <tfoot className="bg-purple-950/30 border-t border-purple-800/30">
                    <tr>
                      <td
                        colSpan={3}
                        className="px-3 py-1.5 text-xs text-purple-400/60"
                      >
                        Total CO impact
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">
                        <span
                          className={
                            row.totalCODeltaQty >= 0
                              ? "text-green-400"
                              : "text-red-400"
                          }
                        >
                          {row.totalCODeltaQty >= 0 ? "+" : ""}
                          {fmtQty(row.totalCODeltaQty)}
                        </span>
                      </td>
                      <td />
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">
                        <span
                          className={
                            row.totalCOAmount >= 0
                              ? "text-purple-300"
                              : "text-red-400"
                          }
                        >
                          {row.totalCOAmount >= 0 ? "+" : ""}$
                          {fmt(row.totalCOAmount)}
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* Piecework log history */}
          <div className="rounded-lg border border-orange-800/40 overflow-hidden">
            <div className="bg-orange-950/30 px-3 py-1.5 border-b border-orange-800/30 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold text-orange-300 uppercase tracking-wide">
                Piecework Log History
              </span>
              <span className="text-[10px] text-orange-700">
                —{" "}
                {logs.length > 0
                  ? `${logs.length} log${logs.length !== 1 ? "s" : ""}${approved.length > 0 ? ` · ${approved.length} approved` : ""}${pending.length > 0 ? ` · ${pending.length} pending` : ""}`
                  : "none yet"}
              </span>
              {/* Pending cost badge — visible at a glance */}
              {pending.length > 0 && (
                <span className="ml-auto text-[10px] bg-yellow-700/40 text-yellow-300 border border-yellow-700/40 px-2 py-0.5 rounded font-semibold">
                  ⏳ ${fmt(pending.reduce((s, l) => s + (l.amount || 0), 0))}{" "}
                  pending approval
                </span>
              )}
            </div>
            {logs.length === 0 ? (
              <p className="px-4 py-3 text-xs text-gray-500 italic">
                No piecework logs recorded for this item yet.
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-orange-950/10">
                  <tr>
                    {[
                      "Log #",
                      "Worker",
                      "Status",
                      "Qty",
                      "Unit Rate",
                      "Subtotal",
                      "Burden",
                      "Total",
                      "Work Date",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-1.5 text-left text-orange-400/60 font-medium"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-orange-900/20">
                  {logs.map((log, i) => {
                    const statusColor =
                      {
                        approved: "bg-green-700/60 text-green-100",
                        rejected: "bg-red-800/60 text-red-200",
                        pending: "bg-yellow-700/60 text-yellow-100",
                      }[log.status] || "bg-gray-700 text-gray-400";
                    const isPending = log.status === "pending";
                    return (
                      <tr
                        key={log._id || i}
                        className={cls(
                          i % 2 === 0 ? "bg-orange-950/5" : "bg-orange-950/10",
                          isPending && "border-l-2 border-yellow-500/50",
                        )}
                      >
                        <td className="px-3 py-1.5 font-mono text-orange-400">
                          {log.logNumber || `LOG-${i + 1}`}
                        </td>
                        <td className="px-3 py-1.5 text-gray-300">
                          {log.workerName || "—"}
                        </td>
                        <td className="px-3 py-1.5">
                          <span
                            className={cls(
                              "px-1.5 py-0.5 text-[9px] font-semibold rounded",
                              statusColor,
                            )}
                          >
                            {(log.status || "pending").toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                          {fmtQty(log.qtyInstalled || 0)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                          ${fmt(log.unitRate || 0)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                          ${fmt(log.subtotal || 0)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                          ${fmt(log.burden || 0)}
                        </td>
                        <td
                          className={cls(
                            "px-3 py-1.5 text-right font-mono font-semibold",
                            isPending ? "text-yellow-300" : "text-blue-300",
                          )}
                        >
                          ${fmt(log.amount || 0)}
                        </td>
                        <td className="px-3 py-1.5 text-gray-500">
                          {log.workCompletedDate
                            ? new Date(
                                log.workCompletedDate,
                              ).toLocaleDateString()
                            : log.submittedAt
                              ? new Date(log.submittedAt).toLocaleDateString()
                              : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Footer: approved totals + pending totals side by side */}
                {logs.length > 0 && (
                  <tfoot className="bg-orange-950/20 border-t border-orange-800/30">
                    {approved.length > 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-3 py-1.5 text-xs text-orange-400/60"
                        >
                          Approved totals ({approved.length})
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs text-gray-400 font-mono">
                          $
                          {fmt(
                            approved.reduce((s, l) => s + (l.subtotal || 0), 0),
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs text-gray-400 font-mono">
                          $
                          {fmt(
                            approved.reduce((s, l) => s + (l.burden || 0), 0),
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right text-sm font-bold text-blue-300 font-mono">
                          $
                          {fmt(
                            approved.reduce((s, l) => s + (l.amount || 0), 0),
                          )}
                        </td>
                        <td />
                      </tr>
                    )}
                    {pending.length > 0 && (
                      <tr className="bg-yellow-900/10">
                        <td
                          colSpan={5}
                          className="px-3 py-1.5 text-xs text-yellow-400/70"
                        >
                          Pending approval ({pending.length}) — not yet charged
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs text-yellow-500/70 font-mono">
                          $
                          {fmt(
                            pending.reduce((s, l) => s + (l.subtotal || 0), 0),
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs text-yellow-500/70 font-mono">
                          $
                          {fmt(
                            pending.reduce((s, l) => s + (l.burden || 0), 0),
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right text-sm font-bold text-yellow-300 font-mono">
                          $
                          {fmt(
                            pending.reduce((s, l) => s + (l.amount || 0), 0),
                          )}
                        </td>
                        <td />
                      </tr>
                    )}
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Section summary builder from consolidated rows ────────────────────────────
function buildSectionSummaries(rows, resolveName) {
  const secMap = new Map();
  rows.forEach((row) => {
    const sec = row.sectionNumber || "(none)";
    if (!secMap.has(sec)) secMap.set(sec, { sectionNumber: sec, rows: [] });
    secMap.get(sec).rows.push(row);
  });

  const summaries = new Map();
  secMap.forEach(({ rows: secRows }, secNum) => {
    const budgetRows = secRows.filter((r) => r.source === "budget");
    const coNewRows = secRows.filter((r) => r.source === "change_order");
    const unbudgetedRows = secRows.filter(
      (r) => r.source === "piecework_unbudgeted",
    );

    const estCost = budgetRows.reduce((s, r) => s + (r.amount || 0), 0);
    const coCost =
      budgetRows.reduce((s, r) => s + (r.totalCOAmount || 0), 0) +
      coNewRows.reduce((s, r) => s + (r.totalCOAmount || 0), 0);
    const runningTotal = estCost + coCost;
    const actualCost = secRows.reduce((s, r) => s + (r.amountActual || 0), 0);
    const pendingCost = secRows.reduce((s, r) => s + (r.amountPending || 0), 0);
    const qtyInstalled = secRows.reduce((s, r) => s + (r.qtyInstalled || 0), 0);
    const qtyEstimated =
      budgetRows.reduce((s, r) => {
        const orig = r.requiredQty || r.quantity || 0;
        return s + orig + (r.totalCODeltaQty || 0);
      }, 0) + coNewRows.reduce((s, r) => s + (r.totalCODeltaQty || 0), 0);
    const estHours = budgetRows.reduce((s, r) => s + (r.laborHours || 0), 0);
    const sectionName = resolveName(secNum);

    summaries.set(secNum, {
      sectionNumber: secNum,
      sectionName,
      itemCount: budgetRows.length + coNewRows.length + unbudgetedRows.length,
      estCost,
      coCost,
      runningTotal,
      actualCost,
      pendingCost,
      qtyEstimated,
      qtyInstalled,
      estHours,
    });
  });

  return summaries;
}

// ═════════════════════════════════════════════════════════════════════════════
// Timesheet charges table — hourly work orders only.
// Lists every timesheet clocked against this WO with worker / status / hours /
// cost breakdown. Columns are sortable and the set is filterable by worker,
// status, and free-text search. A "group by worker" toggle rolls everything
// up so you can see exactly who you paid and how much.
// ═════════════════════════════════════════════════════════════════════════════
function TimesheetChargesTable({ woId, headers }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [workerFilter, setWorkerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("start");
  const [sortDir, setSortDir] = useState("desc");
  const [grouped, setGrouped] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr("");
    axios
      .get(`${API}/workorders3/${woId}/timesheets`, { headers })
      .then((r) => {
        if (active) setRows(r.data.timesheets || []);
      })
      .catch((e) => {
        if (active) setErr(e.response?.data?.message || e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId]);

  const workerOptions = useMemo(() => {
    const s = new Set(rows.map((r) => r.workerName).filter(Boolean));
    return ["all", ...[...s].sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let out = rows.filter((r) => {
      if (workerFilter !== "all" && r.workerName !== workerFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (q && !(r.workerName || "").toLowerCase().includes(q)) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    out = [...out].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === "start" || sortKey === "end") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
        return dir * (av - bv);
      }
      if (sortKey === "workerName" || sortKey === "status") {
        return dir * String(av || "").localeCompare(String(bv || ""));
      }
      return dir * ((av || 0) - (bv || 0));
    });
    return out;
  }, [rows, workerFilter, statusFilter, search, sortKey, sortDir]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => {
          acc.hours += r.hours || 0;
          acc.subtotal += r.subtotal || 0;
          acc.burden += r.burdenCost || 0;
          acc.wsib += r.wsibCost || 0;
          acc.total += r.totalCost || 0;
          if (r.status === "approved") {
            acc.approvedTotal += r.totalCost || 0;
            acc.approvedHours += r.hours || 0;
          }
          if (r.status === "pending") {
            acc.pendingTotal += r.totalCost || 0;
            acc.pendingHours += r.hours || 0;
          }
          return acc;
        },
        {
          hours: 0,
          subtotal: 0,
          burden: 0,
          wsib: 0,
          total: 0,
          approvedTotal: 0,
          approvedHours: 0,
          pendingTotal: 0,
          pendingHours: 0,
        },
      ),
    [filtered],
  );

  const byWorker = useMemo(() => {
    const m = new Map();
    filtered.forEach((r) => {
      if (!m.has(r.workerName))
        m.set(r.workerName, {
          workerName: r.workerName,
          count: 0,
          hours: 0,
          subtotal: 0,
          burden: 0,
          wsib: 0,
          total: 0,
          approved: 0,
          pending: 0,
        });
      const w = m.get(r.workerName);
      w.count += 1;
      w.hours += r.hours || 0;
      w.subtotal += r.subtotal || 0;
      w.burden += r.burdenCost || 0;
      w.wsib += r.wsibCost || 0;
      w.total += r.totalCost || 0;
      if (r.status === "approved") w.approved += r.totalCost || 0;
      if (r.status === "pending") w.pending += r.totalCost || 0;
    });
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [filtered]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
  const fmtTime = (d) =>
    d
      ? new Date(d).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const statusBadge = (status) => {
    const c =
      {
        approved: "bg-green-700/60 text-green-100",
        rejected: "bg-red-800/60 text-red-200",
        pending: "bg-yellow-700/60 text-yellow-100",
      }[status] || "bg-gray-700 text-gray-400";
    return (
      <span
        className={cls("px-1.5 py-0.5 text-[9px] font-semibold rounded", c)}
      >
        {(status || "pending").toUpperCase()}
      </span>
    );
  };

  const SortHeader = ({ label, k, align = "left" }) => (
    <th
      onClick={() => toggleSort(k)}
      className={cls(
        "px-3 py-2.5 text-gray-400 font-medium cursor-pointer select-none hover:text-gray-200 whitespace-nowrap",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {label}
      {sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  const statusFilterOptions = [
    { value: "all", label: "All" },
    { value: "approved", label: "Approved" },
    { value: "pending", label: "Pending" },
    { value: "rejected", label: "Rejected" },
  ];

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">
            Timesheets Charged
          </h3>
          <span className="text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full font-semibold">
            {filtered.length}
          </span>
          {loading && (
            <span className="text-xs text-gray-600 animate-pulse">
              Loading…
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-400">
            Approved hrs:{" "}
            <span className="text-green-300 font-mono font-semibold">
              {totals.approvedHours.toFixed(1)}h
            </span>{" "}
            ·{" "}
            <span className="text-green-300 font-mono font-semibold">
              ${fmt(totals.approvedTotal)}
            </span>
          </span>
          {totals.pendingTotal > 0 && (
            <span className="text-gray-400">
              Committed:{" "}
              <span className="text-yellow-300 font-mono font-semibold">
                {totals.pendingHours.toFixed(1)}h · ${fmt(totals.pendingTotal)}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b border-gray-700/60">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search worker…"
          className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-44"
        />
        <select
          value={workerFilter}
          onChange={(e) => setWorkerFilter(e.target.value)}
          className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
        >
          {workerOptions.map((w) => (
            <option key={w} value={w}>
              {w === "all" ? "All Workers" : w}
            </option>
          ))}
        </select>
        <div className="flex rounded overflow-hidden border border-gray-600">
          {statusFilterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={cls(
                "px-2 py-1 text-xs font-medium transition-colors",
                statusFilter === opt.value
                  ? "bg-orange-500 text-white"
                  : "bg-gray-700 text-gray-400 hover:bg-gray-600",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setGrouped((g) => !g)}
          className={cls(
            "px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors",
            grouped
              ? "bg-indigo-600 text-white"
              : "bg-gray-700 text-gray-400 hover:bg-gray-600",
          )}
        >
          {grouped ? "✓ Grouped by worker" : "Group by worker"}
        </button>
        {(workerFilter !== "all" || statusFilter !== "all" || search) && (
          <button
            onClick={() => {
              setWorkerFilter("all");
              setStatusFilter("all");
              setSearch("");
            }}
            className="px-2 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            ✕ Clear
          </button>
        )}
      </div>

      {err ? (
        <p className="px-5 py-6 text-sm text-red-400">{err}</p>
      ) : !loading && filtered.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">
          {rows.length === 0
            ? "No timesheets have been charged to this work order yet."
            : "No timesheets match your filters."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          {grouped ? (
            // ── Grouped by worker ──
            <table className="w-full text-xs">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-3 py-2.5 text-left text-gray-400 font-medium">
                    Worker
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Sheets
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Hours
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Subtotal
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Burden
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    WSIB
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Approved $
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Pending $
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {byWorker.map((w, i) => (
                  <tr
                    key={w.workerName + i}
                    className={
                      i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"
                    }
                  >
                    <td className="px-3 py-2 text-gray-200 font-medium">
                      {w.workerName}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      {w.count}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-300 font-mono">
                      {w.hours.toFixed(1)}h
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      ${fmt(w.subtotal)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      ${fmt(w.burden)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      ${fmt(w.wsib)}
                    </td>
                    <td className="px-3 py-2 text-right text-green-300 font-mono">
                      {w.approved > 0 ? `$${fmt(w.approved)}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-yellow-300 font-mono">
                      {w.pending > 0 ? `$${fmt(w.pending)}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-white font-mono font-semibold">
                      ${fmt(w.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-900 border-t border-gray-600">
                <tr>
                  <td className="px-3 py-2.5 text-xs text-gray-400">
                    {byWorker.length} worker
                    {byWorker.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                    {filtered.length}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-200 font-mono">
                    {totals.hours.toFixed(1)}h
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                    ${fmt(totals.subtotal)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                    ${fmt(totals.burden)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                    ${fmt(totals.wsib)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-green-300 font-mono">
                    ${fmt(totals.approvedTotal)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-yellow-300 font-mono">
                    {totals.pendingTotal > 0
                      ? `$${fmt(totals.pendingTotal)}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-white font-mono">
                    ${fmt(totals.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            // ── Detailed rows ──
            <table className="w-full text-xs">
              <thead className="bg-gray-900">
                <tr>
                  <SortHeader label="Worker" k="workerName" />
                  <SortHeader label="Status" k="status" />
                  <SortHeader label="Date" k="start" />
                  <th className="px-3 py-2.5 text-left text-gray-400 font-medium">
                    Clock In
                  </th>
                  <th className="px-3 py-2.5 text-left text-gray-400 font-medium">
                    Clock Out
                  </th>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Break
                  </th>
                  <SortHeader label="Hours" k="hours" align="right" />
                  <SortHeader label="Subtotal" k="subtotal" align="right" />
                  <SortHeader label="Burden" k="burdenCost" align="right" />
                  <SortHeader label="WSIB" k="wsibCost" align="right" />
                  <SortHeader label="Total" k="totalCost" align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {filtered.map((r, i) => (
                  <tr
                    key={r._id || i}
                    className={cls(
                      i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                      r.status === "pending" &&
                        "border-l-2 border-yellow-500/50",
                      r.status === "rejected" && "opacity-60",
                    )}
                  >
                    <td className="px-3 py-2 text-gray-200">{r.workerName}</td>
                    <td className="px-3 py-2">{statusBadge(r.status)}</td>
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                      {fmtDate(r.start)}
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {fmtTime(r.start)}
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {fmtTime(r.end)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">
                      {r.breakMins ? `${r.breakMins}m` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-300 font-mono">
                      {(r.hours || 0).toFixed(2)}h
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      ${fmt(r.subtotal)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      ${fmt(r.burdenCost)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 font-mono">
                      ${fmt(r.wsibCost)}
                    </td>
                    <td
                      className={cls(
                        "px-3 py-2 text-right font-mono font-semibold",
                        r.status === "pending"
                          ? "text-yellow-300"
                          : r.status === "approved"
                            ? "text-green-300"
                            : "text-gray-400",
                      )}
                    >
                      ${fmt(r.totalCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-900 border-t border-gray-600">
                <tr>
                  <td colSpan={6} className="px-3 py-2.5 text-xs text-gray-400">
                    {filtered.length} sheet{filtered.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-200 font-mono">
                    {totals.hours.toFixed(1)}h
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                    ${fmt(totals.subtotal)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                    ${fmt(totals.burden)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                    ${fmt(totals.wsib)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-white font-mono">
                    ${fmt(totals.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function BillsChargedTable({ woId, headers }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/bills3`, {
        headers,
        params: { relatedWO3: woId, limit: 200 },
      })
      .then((r) => {
        if (active) setBills(r.data.bills || []);
      })
      .catch((e) => {
        if (active) setErr(e.response?.data?.message || e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [woId]);

  const processed = bills.filter((b) => b.processingStatus === "processed");
  const pending = bills.filter((b) => b.processingStatus === "pending");

  const sumField = (arr, field) => arr.reduce((s, b) => s + (b[field] || 0), 0);

  const statusBadge = (status) => {
    const c =
      status === "processed"
        ? "bg-green-700/60 text-green-100"
        : "bg-yellow-700/60 text-yellow-100";
    return (
      <span
        className={cls("px-1.5 py-0.5 text-[9px] font-semibold rounded", c)}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">Bills Charged</h3>
          <span className="text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full font-semibold">
            {bills.length}
          </span>
          {loading && (
            <span className="text-xs text-gray-600 animate-pulse">
              Loading…
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {processed.length > 0 && (
            <span className="text-gray-400">
              Processed:{" "}
              <span className="text-green-300 font-mono font-semibold">
                ${fmt(sumField(processed, "subtotal"))}
              </span>
            </span>
          )}
          {pending.length > 0 && (
            <span className="text-gray-400">
              Pending:{" "}
              <span className="text-yellow-300 font-mono font-semibold">
                ${fmt(sumField(pending, "subtotal"))}
              </span>
            </span>
          )}
        </div>
      </div>

      {err ? (
        <p className="px-5 py-6 text-sm text-red-400">{err}</p>
      ) : !loading && bills.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">
          No bills have been linked to this work order yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-900">
              <tr>
                {[
                  "Bill Name",
                  "Invoice #",
                  "Invoice Date",
                  "Status",
                  "Subtotal",
                  "Tax",
                  "Total",
                  "Payment",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2.5 text-left text-gray-400 font-medium whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {bills.map((b, i) => (
                <tr
                  key={b._id}
                  className={cls(
                    i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                    b.processingStatus === "pending" &&
                      "border-l-2 border-yellow-500/50",
                  )}
                >
                  <td className="px-3 py-2 text-gray-200 font-medium">
                    {b.billName}
                  </td>
                  <td className="px-3 py-2 text-gray-400 font-mono">
                    {b.invoiceNumber}
                  </td>
                  <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                    {b.invoiceDate
                      ? new Date(b.invoiceDate).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {statusBadge(b.processingStatus)}
                  </td>
                  <td
                    className={cls(
                      "px-3 py-2 text-right font-mono font-semibold",
                      b.processingStatus === "processed"
                        ? "text-green-300"
                        : "text-yellow-300",
                    )}
                  >
                    ${fmt(b.subtotal)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-400 font-mono">
                    ${fmt(b.tax)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-300 font-mono">
                    ${fmt(b.total)}
                  </td>
                  <td className="px-3 py-2">
                    {b.invoiceStatus && (
                      <span
                        className={cls(
                          "px-1.5 py-0.5 text-[9px] font-semibold rounded",
                          b.invoiceStatus === "paid"
                            ? "bg-blue-700/60 text-blue-100"
                            : b.invoiceStatus === "partial"
                              ? "bg-purple-700/60 text-purple-100"
                              : "bg-gray-700 text-gray-400",
                        )}
                      >
                        {b.invoiceStatus.toUpperCase()}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-900 border-t border-gray-600">
              <tr>
                <td colSpan={4} className="px-3 py-2.5 text-xs text-gray-400">
                  {processed.length} processed · {pending.length} pending
                </td>
                <td className="px-3 py-2.5 text-right text-sm font-bold text-green-300 font-mono">
                  ${fmt(sumField(processed, "subtotal"))}
                  {pending.length > 0 && (
                    <span className="block text-xs text-yellow-300">
                      +${fmt(sumField(pending, "subtotal"))} pending
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                  ${fmt(sumField(bills, "tax"))}
                </td>
                <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                  ${fmt(sumField(bills, "total"))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
const COL_COUNT = 14;

export default function WorkOrderView3() {
  const { id: projectId, woId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [wo, setWo] = useState(null);
  const [items, setItems] = useState([]);
  const [logHistory, setLogHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState("");

  const [timesheetSummary, setTimesheetSummary] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const { resolveName, resolveLabel } = useCostCodeMap();

  // Re-fetches just the WO header (actual / committed rollups). Passed to the
  // sub-agreements panel so the cost cards update after create / complete / delete.
  const refreshWo = () =>
    axios
      .get(`${API}/workorders3/${woId}`, { headers })
      .then((r) => setWo(r.data.workOrder))
      .catch(() => {});

  // 1. WO header
  useEffect(() => {
    axios
      .get(`${API}/workorders3/${woId}`, { headers })
      .then((r) => setWo(r.data.workOrder))
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [woId]);

  // 2. Items + log history in parallel
  useEffect(() => {
    setItemsLoading(true);
    setHistoryLoading(true);
    Promise.all([
      axios.get(`${API}/workorders3/${woId}/items`, {
        headers,
        params: { limit: 1000 },
      }),
      axios
        .get(`${API}/workorders3/${woId}/log-history`, { headers })
        .catch(() => ({ data: { historyByBudgetItem: {} } })),
    ])
      .then(([itemsRes, histRes]) => {
        setItems(itemsRes.data.items || []);
        setLogHistory(histRes.data.historyByBudgetItem || {});
      })
      .catch((e) => console.error("WO items load error:", e.message))
      .finally(() => {
        setItemsLoading(false);
        setHistoryLoading(false);
      });
  }, [woId]);

  const [billSummary, setBillSummary] = useState(null);
  const [billsLoading, setBillsLoading] = useState(false);

  useEffect(() => {
    const isSubcontractor = wo && wo.type === "subcontractor";
    const isHourly = wo && (wo.type === "hourly" || wo.type === "union_hourly");

    if (isHourly) {
      setTsLoading(true);
      axios
        .get(`${API}/workorders3/${woId}/timesheet-summary`, { headers })
        .then((r) => setTimesheetSummary(r.data))
        .catch((e) => {
          console.warn("Timesheet summary fetch failed:", e.message);
          setTimesheetSummary(null);
        })
        .finally(() => setTsLoading(false));
    } else {
      setTimesheetSummary(null);
    }

    if (isSubcontractor) {
      setBillsLoading(true);
      axios
        .get(`${API}/workorders3/${woId}/bill-summary`, { headers })
        .then((r) => setBillSummary(r.data))
        .catch((e) => {
          console.warn("Bill summary fetch failed:", e.message);
          setBillSummary(null);
        })
        .finally(() => setBillsLoading(false));
    } else {
      setBillSummary(null);
    }
  }, [wo?.type, woId]);

  const budgetCost = useMemo(
    () =>
      items
        .filter((i) => i.source === "budget")
        .reduce((s, i) => s + (i.amount || 0), 0),
    [items],
  );

  // ── Derived ──────────────────────────────────────────────────────────────

  const uniqueSections = useMemo(() => {
    const s = new Set(items.map((i) => i.sectionNumber).filter(Boolean));
    return ["all", ...[...s].sort()];
  }, [items]);

  const hasCORows = useMemo(
    () => items.some((i) => i.source === "change_order"),
    [items],
  );
  const hasUnbudgeted = useMemo(
    () => items.some((i) => i.source === "piecework_unbudgeted"),
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((item) => {
      if (sectionFilter !== "all" && item.sectionNumber !== sectionFilter)
        return false;
      if (sourceFilter !== "all") {
        if (sourceFilter === "budget" && item.source !== "budget") return false;
        if (sourceFilter === "change_order" && item.source !== "change_order")
          return false;
        if (
          sourceFilter === "unbudgeted" &&
          item.source !== "piecework_unbudgeted"
        )
          return false;
      }
      if (
        q &&
        !(item.costCodeDescription || item.itemDescription || "")
          .toLowerCase()
          .includes(q) &&
        !(item.itemCode || "").toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [items, search, sectionFilter, sourceFilter]);

  const sectionSummaries = useMemo(
    () => buildSectionSummaries(items, resolveName),
    [items, resolveName],
  );

  const footerTotals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => {
          if (r.source === "budget") {
            acc.estimated += (r.amount || 0) + (r.totalCOAmount || 0);
            acc.hours += r.laborHours || 0;
          } else if (r.source === "change_order") {
            // CO-new rows aren't in a budget row — their net total is the CO amount itself
            acc.estimated += r.totalCOAmount || 0;
          }
          acc.installed += r.qtyInstalled || 0;
          acc.amountActual += r.amountActual || 0;
          acc.amountPending += r.amountPending || 0;
          return acc;
        },
        {
          estimated: 0,
          hours: 0,
          installed: 0,
          amountActual: 0,
          amountPending: 0,
        },
      ),
    [filtered],
  );

  const sectionList = useMemo(
    () =>
      [...sectionSummaries.values()].sort((a, b) =>
        (a.sectionNumber || "").localeCompare(b.sectionNumber || ""),
      ),
    [sectionSummaries],
  );

  // ── Early returns ─────────────────────────────────────────────────────────

  if (loading)
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  if (error)
    return <div className="text-center py-10 text-red-400">{error}</div>;
  if (!wo) return null;

  // ── WO type flags ───────────────────────────────────────────────────────
  const isPiecework = wo.type === "piecework" || wo.type === "union_piecework";
  const isHourly = wo.type === "hourly" || wo.type === "union_hourly";

  // ── WO header values ──────────────────────────────────────────────────────

  const originalEstimated = wo.originalEstimatedAmount || 0;
  const changeOrderAmount = wo.changeOrderAmount || 0;
  const runningTotal = wo.runningTotal || originalEstimated + changeOrderAmount;

  // ── Estimated hours + carried-forward CO hours ────────────────────────────
  // We derive an estimated blended hourly rate from the original estimate and
  // the budgeted labour hours, then convert the change-order dollar impact back
  // into hours so the hours picture carries forward with the CO scope changes.
  const estHours = items
    .filter((i) => i.source === "budget")
    .reduce((s, i) => s + (i.laborHours || 0), 0);
  const estHourlyRate = estHours > 0 ? originalEstimated / estHours : 0;
  const coHours = estHourlyRate > 0 ? changeOrderAmount / estHourlyRate : 0;
  const totalEstHours = estHours + coHours;

  // ── Timesheet figures (hourly) ────────────────────────────────────────────
  const tsApprovedCost = timesheetSummary?.approved?.totalCost || 0;
  const tsPendingCost = timesheetSummary?.pending?.totalCost || 0;
  const tsApprovedHrs = timesheetSummary?.approved?.hours || 0;
  const tsPendingHrs = timesheetSummary?.pending?.hours || 0;

  // ── Type-aware actual + committed (pending) ───────────────────────────────
  // Hourly WOs are charged via timesheets; piecework via piecework-log deltas.
  const itemsActual = items.reduce((s, i) => s + (i.amountActual || 0), 0);
  const itemsPending = items.reduce((s, i) => s + (i.amountPending || 0), 0);

  const isSubcontractor = wo.type === "subcontractor";

  // Sub agreements: completed/paid = committed (counts as actual),
  // issued = incurred (counts as pending/committed-awaiting).
  const subAgCommitted = wo.subAgreementCommitted || 0;
  const subAgIncurred = wo.subAgreementIncurred || 0;

  const billProcessedAmt = billSummary?.processed?.subtotal || 0;
  const billPendingAmt = billSummary?.pending?.subtotal || 0;

  // Base cost by WO type, then sub agreements layered on top for EVERY type
  // (a piecework / hourly / subcontractor WO can all carry agreements).
  const baseActual = isHourly
    ? tsApprovedCost
    : isSubcontractor
      ? billProcessedAmt
      : itemsActual;
  const basePending = isHourly
    ? tsPendingCost
    : isSubcontractor
      ? billPendingAmt
      : itemsPending;

  const actualAmount = baseActual + subAgCommitted;
  const pendingAmount = basePending + subAgIncurred;

  const flag =
    actualAmount > 0 ? (runningTotal >= actualAmount ? "green" : "red") : null;

  const woTypeLabel = WO_TYPE_LABELS[wo.type] || wo.type;
  const woTypeColor = WO_TYPE_COLORS[wo.type] || "bg-gray-700 text-gray-400";
  const unionLabel = wo.union && wo.union !== "non-union" ? wo.union : null;

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const sourceFilterOptions = [
    { value: "all", label: "All" },
    { value: "budget", label: "Budget" },
    ...(hasCORows ? [{ value: "change_order", label: "CO Δ" }] : []),
    ...(hasUnbudgeted ? [{ value: "unbudgeted", label: "Unbudgeted" }] : []),
  ];

  const renderEntries = [];
  filtered.forEach((row, idx) => {
    renderEntries.push({ kind: "item", row, idx, key: row._id?.toString() });
    if (expandedId === row._id?.toString()) {
      renderEntries.push({ kind: "history", row, key: `${row._id}-hist` });
    }
  });

  return (
    <div className="max-w-full mx-auto px-4 py-6 space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate(`/Projects/${projectId}?tab=Work Orders`)}
          className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold text-gray-800">{wo.title}</h1>
            <span
              className={cls(
                "px-2 py-0.5 text-[10px] font-semibold rounded-full",
                woTypeColor,
              )}
            >
              {woTypeLabel}
            </span>
            {isHourly && (
              <span className="text-[10px] text-blue-400 bg-blue-900 px-1.5 py-0.5 rounded">
                Charged via Timesheets
              </span>
            )}
            {isPiecework && (
              <span className="text-[10px] text-white bg-orange-900 px-1.5 py-0.5 rounded">
                Charged via Piecework Logs
              </span>
            )}
            {isSubcontractor && (
              <span className="text-[10px] text-violet-200 bg-violet-900 px-1.5 py-0.5 rounded">
                Charged via Agreements &amp; Bills
              </span>
            )}
            {(subAgCommitted > 0 || subAgIncurred > 0) && !isSubcontractor && (
              <span className="text-[10px] text-violet-200 bg-violet-900 px-1.5 py-0.5 rounded">
                + Sub Agreements
              </span>
            )}
            {unionLabel && (
              <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-teal-700 text-white">
                {unionLabel}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-800 mt-0.5">
            {wo.woNumber} · Floor:{" "}
            <span className="text-gray-800">{wo.bidArea || "All Areas"}</span>
            {wo.sectionCount > 0 && (
              <span className="ml-2 text-gray-800">
                · {wo.sectionCount} section{wo.sectionCount !== 1 ? "s" : ""}
              </span>
            )}
            <span
              className={cls(
                "ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full",
                wo.status === "active"
                  ? "bg-green-700 text-white"
                  : "bg-gray-700 text-white",
              )}
            >
              {wo.status}
            </span>
            {(itemsLoading || historyLoading) && (
              <span className="ml-2 text-[10px] text-gray-600 animate-pulse">
                Loading…
              </span>
            )}
          </p>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <SummaryCard
          label="Original Estimated"
          value={originalEstimated}
          color="text-white"
        />
        <SummaryCard
          label="Change Orders"
          value={changeOrderAmount}
          color="text-purple-300"
        />
        <SummaryCard
          label="Running Total"
          value={runningTotal}
          color="text-orange-300"
        />

        {/* Actual Charged — approved cost only */}
        <SummaryCard
          label="Actual Charged"
          value={actualAmount}
          color={
            flag === "green"
              ? "text-green-300"
              : flag === "red"
                ? "text-red-400"
                : "text-blue-300"
          }
          flag={flag}
          sub={
            actualAmount > 0
              ? `Δ $${fmt(Math.abs(runningTotal - actualAmount))} ${flag === "green" ? "under" : "over"}`
              : undefined
          }
        />

        {/* Committed Costs — pending / awaiting approval, promoted to its own card */}
        <SummaryCard
          label="Committed Costs"
          value={pendingAmount}
          color={pendingAmount > 0 ? "text-yellow-300" : "text-gray-500"}
          flag={pendingAmount > 0 ? "yellow" : undefined}
          sub={pendingAmount > 0 ? "Pending approval" : "Nothing pending"}
          sub2={
            pendingAmount > 0
              ? `Projected $${fmt(actualAmount + pendingAmount)}`
              : undefined
          }
        />

        {/* Est. Hours — now carries forward CO hours + actual/committed hours */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
            Est. Hours
          </p>
          <p className="text-xl font-bold text-white font-mono">
            {totalEstHours.toFixed(1)}
          </p>
          {coHours !== 0 && (
            <p className="text-[10px] text-purple-300 font-mono mt-0.5">
              {estHours.toFixed(1)} {coHours >= 0 ? "+" : "−"}
              {Math.abs(coHours).toFixed(1)} CO
            </p>
          )}
          {isHourly ? (
            <div className="mt-1 space-y-0.5">
              <p className="text-[10px] text-green-400 font-mono">
                Charged: {tsApprovedHrs.toFixed(1)}h
              </p>
              {tsPendingHrs > 0 && (
                <p className="text-[10px] text-yellow-400 font-mono">
                  ⏳ +{tsPendingHrs.toFixed(1)}h committed
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5">
              Installed:{" "}
              {fmtQty(items.reduce((s, i) => s + (i.qtyInstalled || 0), 0))}
            </p>
          )}
        </div>

        <div
          className={cls(
            "rounded-xl border p-4",
            flag === "green"
              ? "bg-green-950 border-green-700"
              : flag === "red"
                ? "bg-red-950 border-red-700"
                : "bg-gray-800 border-gray-700",
          )}
        >
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
            Status {flag === "green" ? "✓" : flag === "red" ? "⚠" : ""}
          </p>
          <p
            className={cls(
              "text-sm font-bold",
              flag === "green"
                ? "text-green-400"
                : flag === "red"
                  ? "text-red-400"
                  : "text-gray-400",
            )}
          >
            {actualAmount === 0
              ? "No actuals yet"
              : flag === "green"
                ? "Under budget"
                : "Over budget"}
          </p>
          {actualAmount > 0 && (
            <p
              className={cls(
                "text-xs mt-0.5",
                flag === "green" ? "text-green-500" : "text-red-500",
              )}
            >
              Δ ${fmt(Math.abs(runningTotal - actualAmount))}
            </p>
          )}
          {pendingAmount > 0 && (
            <p className="text-xs text-yellow-500 mt-0.5">
              ⏳ ${fmt(pendingAmount)} committed
            </p>
          )}
        </div>
      </div>

      {isHourly && (timesheetSummary || tsLoading) && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">Cost Summary</h3>
            {tsLoading && (
              <span className="text-xs text-gray-600 animate-pulse">
                Loading…
              </span>
            )}
          </div>

          {timesheetSummary &&
            (() => {
              // Timesheets + sub agreements on this WO
              const approvedCost =
                (timesheetSummary.approved?.totalCost || 0) + subAgCommitted;
              const pendingCost =
                (timesheetSummary.pending?.totalCost || 0) + subAgIncurred;
              const approvedHrs = timesheetSummary.approved?.hours || 0;
              const pendingHrs = timesheetSummary.pending?.hours || 0;
              const remaining = approvedCost - budgetCost;
              const isOver = remaining > 0;
              const usedPct =
                budgetCost > 0
                  ? Math.min(999, (approvedCost / budgetCost) * 100)
                  : 0;

              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-700">
                  {/* Budget */}
                  <div className="px-5 py-4 space-y-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                      Estimated Budget
                    </p>
                    <p className="text-2xl font-bold text-white font-mono">
                      ${fmt(budgetCost)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {items.filter((i) => i.source === "budget").length} budget
                      items · {totalEstHours.toFixed(1)} est hrs
                    </p>
                  </div>

                  {/* Actual + committed */}
                  <div className="px-5 py-4 space-y-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                      Actual Charged
                    </p>
                    <p
                      className={cls(
                        "text-2xl font-bold font-mono",
                        approvedCost === 0
                          ? "text-gray-500"
                          : isOver
                            ? "text-red-400"
                            : "text-green-400",
                      )}
                    >
                      ${fmt(approvedCost)}
                    </p>
                    <div className="space-y-1">
                      {approvedHrs > 0 && (
                        <p className="text-xs text-gray-500">
                          {approvedHrs}h ·{" "}
                          {timesheetSummary.approved?.count ?? 0} approved
                          sheets
                          {subAgCommitted > 0 &&
                            ` + $${fmt(subAgCommitted)} agreements`}
                        </p>
                      )}
                      {/* Pending / committed — promoted to a clear highlighted block */}
                      {pendingCost > 0 && (
                        <div className="rounded-lg bg-yellow-950/40 border border-yellow-700/40 px-2.5 py-1.5">
                          <p className="text-[10px] font-semibold text-yellow-400/80 uppercase tracking-wide">
                            ⏳ Committed (pending)
                          </p>
                          <p className="text-base font-bold text-yellow-300 font-mono">
                            ${fmt(pendingCost)}
                          </p>
                          <p className="text-[10px] text-yellow-500/70">
                            {pendingHrs}h ·{" "}
                            {timesheetSummary.pending?.count ?? 0} sheets
                            awaiting approval
                          </p>
                        </div>
                      )}
                      {/* Subtotal / burden / WSIB breakdown */}
                      {(timesheetSummary.approved?.burdenCost || 0) > 0 && (
                        <div className="pt-1 space-y-0.5 text-[11px] text-gray-600">
                          <div className="flex justify-between">
                            <span>Base pay</span>
                            <span>
                              ${fmt(timesheetSummary.approved.subtotal || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Burden</span>
                            <span>
                              ${fmt(timesheetSummary.approved.burdenCost || 0)}
                            </span>
                          </div>
                          {(timesheetSummary.approved?.wsibCost || 0) > 0 && (
                            <div className="flex justify-between">
                              <span>WSIB</span>
                              <span>
                                ${fmt(timesheetSummary.approved.wsibCost || 0)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Variance */}
                  <div className="px-5 py-4 space-y-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                      Remaining
                    </p>
                    {approvedCost === 0 ? (
                      <p className="text-2xl font-bold text-gray-600 font-mono">
                        —
                      </p>
                    ) : (
                      <>
                        <p
                          className={cls(
                            "text-2xl font-bold font-mono",
                            isOver ? "text-red-400" : "text-green-400",
                          )}
                        >
                          {isOver ? "+" : "−"}${fmt(Math.abs(remaining))}
                        </p>
                        <div className="space-y-1.5">
                          <p
                            className={cls(
                              "text-xs font-medium",
                              isOver ? "text-red-400" : "text-green-400",
                            )}
                          >
                            {isOver ? "Over budget" : "Under budget"}
                          </p>
                          {/* Budget usage bar */}
                          <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                            <div
                              className={cls(
                                "h-full rounded-full transition-all",
                                usedPct > 100 ? "bg-red-500" : "bg-orange-400",
                              )}
                              style={{ width: `${Math.min(100, usedPct)}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-500 text-right">
                            {usedPct.toFixed(1)}% of budget used
                          </p>
                          {pendingCost > 0 && (
                            <p
                              className={cls(
                                "text-[10px]",
                                approvedCost + pendingCost > budgetCost
                                  ? "text-red-400/70"
                                  : "text-yellow-500/70",
                              )}
                            >
                              incl. committed:{" "}
                              {budgetCost > 0
                                ? (
                                    ((approvedCost + pendingCost) /
                                      budgetCost) *
                                    100
                                  ).toFixed(1)
                                : "0.0"}
                              % used
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
        </div>
      )}

      {/* ── Subcontractor cost summary — bills + sub agreements ── */}
      {isSubcontractor &&
        (billSummary ||
          billsLoading ||
          subAgCommitted > 0 ||
          subAgIncurred > 0) && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Cost Summary</h3>
              {billsLoading && (
                <span className="text-xs text-gray-600 animate-pulse">
                  Loading…
                </span>
              )}
            </div>
            {(() => {
              // Bills + agreements merged:
              //   processed bills + completed/paid agreements → actual
              //   pending bills   + issued agreements         → committed
              const proc = {
                count: billSummary?.processed?.count || 0,
                subtotal:
                  (billSummary?.processed?.subtotal || 0) + subAgCommitted,
              };
              const pend = {
                count: billSummary?.pending?.count || 0,
                subtotal: (billSummary?.pending?.subtotal || 0) + subAgIncurred,
              };
              const remaining = runningTotal - proc.subtotal;
              const isOver = remaining < 0;
              const usedPct =
                runningTotal > 0
                  ? Math.min(999, (proc.subtotal / runningTotal) * 100)
                  : 0;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-700">
                  <div className="px-5 py-4 space-y-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                      Budget
                    </p>
                    <p className="text-2xl font-bold text-white font-mono">
                      ${fmt(runningTotal)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {items.filter((i) => i.source === "budget").length} budget
                      items
                    </p>
                  </div>
                  <div className="px-5 py-4 space-y-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                      Actual Charged
                    </p>
                    <p
                      className={cls(
                        "text-2xl font-bold font-mono",
                        proc.subtotal === 0
                          ? "text-gray-500"
                          : isOver
                            ? "text-red-400"
                            : "text-green-400",
                      )}
                    >
                      ${fmt(proc.subtotal)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {proc.count} processed bill{proc.count !== 1 ? "s" : ""}
                      {subAgCommitted > 0 &&
                        ` + $${fmt(subAgCommitted)} completed agreements`}
                    </p>
                    {pend.subtotal > 0 && (
                      <div className="rounded-lg bg-yellow-950/40 border border-yellow-700/40 px-2.5 py-1.5">
                        <p className="text-[10px] font-semibold text-yellow-400/80 uppercase tracking-wide">
                          ⏳ Committed (pending)
                        </p>
                        <p className="text-base font-bold text-yellow-300 font-mono">
                          ${fmt(pend.subtotal)}
                        </p>
                        <p className="text-[10px] text-yellow-500/70">
                          {pend.count} bill{pend.count !== 1 ? "s" : ""}{" "}
                          awaiting processing
                          {subAgIncurred > 0 &&
                            ` · $${fmt(subAgIncurred)} issued agreements`}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="px-5 py-4 space-y-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                      Remaining
                    </p>
                    {proc.subtotal === 0 ? (
                      <p className="text-2xl font-bold text-gray-600 font-mono">
                        —
                      </p>
                    ) : (
                      <>
                        <p
                          className={cls(
                            "text-2xl font-bold font-mono",
                            isOver ? "text-red-400" : "text-green-400",
                          )}
                        >
                          {isOver ? "+" : "−"}${fmt(Math.abs(remaining))}
                        </p>
                        <p
                          className={cls(
                            "text-xs font-medium",
                            isOver ? "text-red-400" : "text-green-400",
                          )}
                        >
                          {isOver ? "Over budget" : "Under budget"}
                        </p>
                        <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={cls(
                              "h-full rounded-full transition-all",
                              usedPct > 100 ? "bg-red-500" : "bg-orange-400",
                            )}
                            style={{ width: `${Math.min(100, usedPct)}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-500 text-right">
                          {usedPct.toFixed(1)}% of budget used
                        </p>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b border-gray-700">
        {[
          {
            id: "summary",
            label: "Section Summary",
            count: sectionList.length,
          },
          { id: "items", label: "Line Items", count: filtered.length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cls(
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
              activeTab === tab.id
                ? "border-orange-500 text-orange-400"
                : "border-transparent text-gray-500 hover:text-gray-300",
            )}
          >
            {tab.label}
            <span
              className={cls(
                "ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold",
                activeTab === tab.id
                  ? "bg-orange-500/20 text-orange-300"
                  : "bg-gray-700 text-gray-500",
              )}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SUMMARY TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "summary" && (
        <div className="space-y-3">
          {sectionList.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10">
              No sections found.
            </p>
          ) : (
            sectionList.map((sec) => {
              const installedPct =
                sec.qtyEstimated > 0
                  ? Math.min(100, (sec.qtyInstalled / sec.qtyEstimated) * 100)
                  : 0;
              const costPct =
                sec.runningTotal > 0
                  ? Math.min(100, (sec.actualCost / sec.runningTotal) * 100)
                  : 0;
              // Combined (approved + pending) vs budget
              const combinedPct =
                sec.runningTotal > 0
                  ? Math.min(
                      100,
                      ((sec.actualCost + sec.pendingCost) / sec.runningTotal) *
                        100,
                    )
                  : 0;
              const remaining = sec.actualCost - sec.runningTotal;

              return (
                <div
                  key={sec.sectionNumber}
                  className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden"
                >
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-700">
                    <span className="text-[11px] font-mono text-indigo-400 bg-indigo-900 px-2 py-0.5 rounded shrink-0">
                      §{sec.sectionNumber}
                    </span>
                    <span className="text-sm font-semibold text-white flex-1 truncate">
                      {sec.sectionName || sec.sectionNumber}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {sec.itemCount} item{sec.itemCount !== 1 ? "s" : ""}
                    </span>
                    {sec.estHours > 0 && (
                      <span className="text-[10px] text-gray-500 shrink-0">
                        · {sec.estHours.toFixed(1)} est hrs
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setSectionFilter(sec.sectionNumber);
                        setActiveTab("items");
                      }}
                      className="text-[10px] text-orange-400 hover:text-orange-300 shrink-0 px-2 py-0.5 rounded bg-orange-900 hover:bg-orange-900/40 transition-colors"
                    >
                      View items →
                    </button>
                  </div>
                  <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                        Estimated
                      </p>
                      <p className="text-sm font-bold font-mono text-blue-300">
                        ${fmt(sec.estCost)}
                      </p>
                      {sec.coCost !== 0 && (
                        <p
                          className={cls(
                            "text-[10px] font-mono mt-0.5",
                            sec.coCost >= 0
                              ? "text-purple-400"
                              : "text-red-400",
                          )}
                        >
                          {sec.coCost >= 0 ? "+" : ""}${fmt(sec.coCost)} CO
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                        Net Budget
                      </p>
                      <p className="text-sm font-bold font-mono text-orange-300">
                        ${fmt(sec.runningTotal)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                        Actual Charged
                      </p>
                      <p className="text-sm font-bold font-mono text-green-300">
                        ${fmt(sec.actualCost)}
                      </p>
                      {/* Pending cost shown clearly beneath actual */}
                      {sec.pendingCost > 0 && (
                        <p className="text-[10px] font-mono text-yellow-400 mt-0.5 flex items-center gap-1">
                          <span>⏳</span>
                          <span>+${fmt(sec.pendingCost)} committed</span>
                        </p>
                      )}
                      {sec.actualCost > 0 && (
                        <p
                          className={cls(
                            "text-[10px] font-mono mt-0.5",
                            remaining > 0 ? "text-red-400" : "text-green-500",
                          )}
                        >
                          {remaining > 0 ? "↑ over" : "↓ under"} $
                          {fmt(Math.abs(remaining))}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                        Installed
                      </p>
                      <p className="text-sm font-bold font-mono text-purple-300">
                        {fmtQty(sec.qtyInstalled)}
                        <span className="text-gray-500 font-normal text-[11px]">
                          {" "}
                          / {fmtQty(sec.qtyEstimated)}
                        </span>
                      </p>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {installedPct.toFixed(0)}% complete
                      </p>
                    </div>
                  </div>
                  <div className="px-4 pb-3 space-y-1.5">
                    {/* $ Charged bar — shows approved (solid) + pending (ghost) */}
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-gray-600 w-20 shrink-0">
                        $ Charged
                      </span>
                      <div className="relative flex-1 bg-gray-700 rounded-full h-1.5 overflow-hidden">
                        {/* pending ghost */}
                        {sec.pendingCost > 0 && (
                          <div
                            className="absolute left-0 top-0 h-full bg-yellow-500/30 rounded-full"
                            style={{ width: `${combinedPct}%` }}
                          />
                        )}
                        <div
                          className={cls(
                            "absolute left-0 top-0 h-full rounded-full transition-all",
                            costPct >= 100 ? "bg-red-500" : "bg-orange-500",
                          )}
                          style={{ width: `${costPct}%` }}
                        />
                      </div>
                      <span className="text-[9px] text-gray-500 font-mono w-8 text-right shrink-0">
                        {costPct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-gray-600 w-20 shrink-0">
                        Installed
                      </span>
                      <div className="flex-1 bg-gray-700 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={cls(
                            "h-full rounded-full transition-all",
                            installedPct >= 100
                              ? "bg-green-500"
                              : "bg-purple-500",
                          )}
                          style={{ width: `${installedPct}%` }}
                        />
                      </div>
                      <span className="text-[9px] text-gray-500 font-mono w-8 text-right shrink-0">
                        {installedPct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          ITEMS TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "items" && (
        <>
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />
              Budget
            </span>
            {hasCORows && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" />
                CO Δ merged into row
              </span>
            )}
            {hasUnbudgeted && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />
                Unbudgeted
              </span>
            )}
            <span className="ml-auto text-gray-600">
              ▶ Click row to see log history
            </span>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items…"
              className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-52"
            />
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
            >
              {uniqueSections.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All Sections" : resolveLabel(s)}
                </option>
              ))}
            </select>
            {sectionFilter !== "all" && (
              <button
                onClick={() => setSectionFilter("all")}
                className="px-2 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              >
                ✕ Clear
              </button>
            )}
            <div className="flex rounded overflow-hidden border border-gray-600">
              {sourceFilterOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSourceFilter(opt.value)}
                  className={cls(
                    "px-2 py-1 text-xs font-medium transition-colors",
                    sourceFilter === opt.value
                      ? "bg-orange-500 text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-500">
              {filtered.length} of {items.length} items
            </span>
          </div>

          {/* Section context badge */}
          {sectionFilter !== "all" &&
            sectionSummaries.has(sectionFilter) &&
            (() => {
              const sec = sectionSummaries.get(sectionFilter);
              return (
                <div className="flex items-center gap-3 flex-wrap bg-gray-700 border border-indigo-800 rounded-xl px-4 py-2.5">
                  <span className="text-[11px] font-mono text-indigo-400 bg-indigo-900 px-2 py-0.5 rounded shrink-0">
                    §{sec.sectionNumber}
                  </span>
                  <span className="text-sm font-semibold text-white">
                    {sec.sectionName || sec.sectionNumber}
                  </span>
                  <div className="h-4 border-l border-white" />
                  <span className="text-xs text-white">
                    Est:{" "}
                    <span className="text-blue-300 font-mono font-semibold">
                      ${fmt(sec.estCost)}
                    </span>
                  </span>
                  {sec.coCost !== 0 && (
                    <span className="text-xs text-gray-400">
                      CO:{" "}
                      <span
                        className={cls(
                          "font-mono font-semibold",
                          sec.coCost >= 0 ? "text-purple-300" : "text-red-400",
                        )}
                      >
                        {sec.coCost >= 0 ? "+" : ""}${fmt(sec.coCost)}
                      </span>
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    Net:{" "}
                    <span className="text-orange-300 font-mono font-semibold">
                      ${fmt(sec.runningTotal)}
                    </span>
                  </span>
                  <span className="text-xs text-gray-400">
                    Charged:{" "}
                    <span className="text-green-300 font-mono font-semibold">
                      ${fmt(sec.actualCost)}
                    </span>
                  </span>
                  {sec.pendingCost > 0 && (
                    <span className="text-xs text-gray-400">
                      Committed:{" "}
                      <span className="text-yellow-300 font-mono font-semibold">
                        ${fmt(sec.pendingCost)}
                      </span>
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    Installed:{" "}
                    <span className="text-purple-300 font-mono font-semibold">
                      {fmtQty(sec.qtyInstalled)} / {fmtQty(sec.qtyEstimated)}
                    </span>
                  </span>
                </div>
              );
            })()}

          {/* Table */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-900 sticky top-0 z-20">
                  <tr>
                    <th className="px-2 py-2.5 w-6" />
                    <th className="px-3 py-2.5 text-left text-gray-400 font-medium">
                      Code
                    </th>
                    <th className="px-3 py-2.5 text-left text-gray-400 font-medium">
                      Section
                    </th>
                    <th className="px-3 py-2.5 text-left text-gray-400 font-medium min-w-[180px]">
                      Description
                    </th>
                    <th className="px-3 py-2.5 text-center text-gray-400 font-medium">
                      UOM
                    </th>
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Net Qty
                      <span className="block text-[9px] text-gray-600 font-normal normal-case">
                        budget + CO Δ
                      </span>
                    </th>
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Unit Rate
                    </th>
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Est. Hrs
                    </th>
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Net Total
                      <span className="block text-[9px] text-gray-600 font-normal normal-case">
                        budget + CO
                      </span>
                    </th>
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Installed
                    </th>
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Pending
                    </th>
                    {/* Split Actual $ into approved + pending columns */}
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Actual $
                      <span className="block text-[9px] text-gray-600 font-normal">
                        approved
                      </span>
                    </th>
                    <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                      Committed $
                      <span className="block text-[9px] text-yellow-600/60 font-normal">
                        awaiting
                      </span>
                    </th>
                    <th className="px-3 py-2.5 text-left text-gray-400 font-medium min-w-[130px]">
                      Progress
                    </th>
                    <th className="px-3 py-2.5 text-left text-gray-400 font-medium">
                      Type
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-700/50">
                  {renderEntries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COL_COUNT + 1}
                        className="px-3 py-10 text-center text-gray-500"
                      >
                        {items.length === 0 && !itemsLoading
                          ? "No labour items on this work order yet."
                          : "No items match your filters."}
                      </td>
                    </tr>
                  ) : (
                    renderEntries.map((entry) => {
                      if (entry.kind === "history") {
                        return (
                          <ExpandedDetailPanel
                            key={entry.key}
                            row={entry.row}
                            logHistory={logHistory}
                            colSpan={COL_COUNT + 1}
                          />
                        );
                      }

                      const { row, idx } = entry;
                      const isBudget = row.source === "budget";
                      const isCONew = row.source === "change_order";
                      const isUnbudgeted =
                        row.source === "piecework_unbudgeted";
                      const isExpanded = expandedId === row._id?.toString();

                      const origQty = row.requiredQty || row.quantity || 0;
                      const netQty = isBudget
                        ? origQty + (row.totalCODeltaQty || 0)
                        : isCONew
                          ? row.totalCODeltaQty || 0
                          : row.qtyInstalled || 0;
                      const origCost = row.amount || 0;
                      const netCost = isBudget
                        ? origCost + (row.totalCOAmount || 0)
                        : isCONew
                          ? row.totalCOAmount || 0
                          : 0;

                      const netQtyChanged =
                        isBudget && (row.totalCODeltaQty || 0) !== 0;
                      const hasCOs = (row._coRows || []).length > 0;
                      const bgBase =
                        idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20";

                      return (
                        <tr
                          key={entry.key}
                          onClick={() => toggleExpand(row._id?.toString())}
                          className={cls(
                            "cursor-pointer hover:bg-gray-600/30 transition-colors select-none",
                            bgBase,
                            isCONew ? "border-l-2 border-purple-600/60" : "",
                            isUnbudgeted
                              ? "border-l-2 border-yellow-600/60"
                              : "",
                            isExpanded ? "bg-indigo-900/20" : "",
                          )}
                        >
                          <td className="px-2 py-2.5 text-center">
                            <span
                              className={cls(
                                "inline-block text-[10px] transition-transform duration-150",
                                isExpanded
                                  ? "rotate-90 text-orange-400"
                                  : "text-gray-400",
                              )}
                            >
                              ▶
                            </span>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-orange-400">
                            {row.itemCode || "—"}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-[9px] font-mono text-indigo-400 bg-indigo-900/40 px-1.5 py-0.5 rounded whitespace-nowrap">
                              {row.sectionNumber || "—"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-200">
                            {row.costCodeDescription ||
                              row.itemDescription ||
                              "—"}
                            {hasCOs && (
                              <span className="ml-1.5 text-[9px] bg-purple-800/50 text-purple-300 px-1 py-0.5 rounded font-semibold whitespace-nowrap">
                                CO {row.totalCODeltaQty >= 0 ? "+" : ""}
                                {fmtQty(row.totalCODeltaQty)}
                              </span>
                            )}
                            {isCONew && (
                              <span className="ml-1.5 text-[9px] bg-purple-600/60 text-purple-100 px-1 py-0.5 rounded font-semibold">
                                CO NEW
                              </span>
                            )}
                            {isUnbudgeted && (
                              <span className="ml-1.5 text-[9px] bg-yellow-600/60 text-yellow-100 px-1 py-0.5 rounded font-semibold">
                                UNBUDGETED
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-400">
                            {row.uom || "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono">
                            {isUnbudgeted ? (
                              <span className="text-gray-600">—</span>
                            ) : netQtyChanged ? (
                              <span className="flex flex-col items-end leading-tight">
                                <span className="text-orange-300 font-semibold">
                                  {fmtQty(netQty)}
                                </span>
                                <span className="text-[9px] text-gray-500">
                                  {fmtQty(origQty)}{" "}
                                  <span
                                    className={
                                      row.totalCODeltaQty > 0
                                        ? "text-green-500"
                                        : "text-red-500"
                                    }
                                  >
                                    {row.totalCODeltaQty > 0 ? "+" : ""}
                                    {fmtQty(row.totalCODeltaQty)}
                                  </span>
                                </span>
                              </span>
                            ) : (
                              <span className="text-gray-300">
                                {fmtQty(netQty)}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-300 font-mono">
                            ${fmt(row.unitRate || 0)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-400 font-mono">
                            {isBudget ? (row.laborHours || 0).toFixed(1) : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono">
                            {isUnbudgeted ? (
                              <span className="text-yellow-500 text-[10px]">
                                $0 est.
                              </span>
                            ) : (
                              <span className="flex flex-col items-end leading-tight">
                                <span
                                  className={cls(
                                    "font-semibold",
                                    netQtyChanged
                                      ? "text-orange-300"
                                      : isCONew
                                        ? netCost >= 0
                                          ? "text-purple-300"
                                          : "text-red-400"
                                        : "text-blue-300",
                                  )}
                                >
                                  {isCONew && netCost >= 0 ? "+" : ""}$
                                  {fmt(netCost)}
                                </span>
                                {hasCOs && (row.totalCOAmount || 0) !== 0 && (
                                  <span
                                    className={cls(
                                      "text-[9px]",
                                      row.totalCOAmount > 0
                                        ? "text-purple-400"
                                        : "text-red-400",
                                    )}
                                  >
                                    {row.totalCOAmount > 0 ? "+" : ""}$
                                    {fmt(row.totalCOAmount)} CO
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right text-green-300 font-mono">
                            {fmtQty(row.qtyInstalled || 0)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-yellow-400 font-mono">
                            {(row.qtyPending || 0) > 0 ? (
                              fmtQty(row.qtyPending)
                            ) : (
                              <span className="text-gray-700">—</span>
                            )}
                          </td>
                          {/* Actual $ — approved only */}
                          <td className="px-3 py-2.5 text-right font-mono font-semibold">
                            {(row.amountActual || 0) > 0 ? (
                              <span className="text-orange-300">
                                ${fmt(row.amountActual)}
                              </span>
                            ) : (
                              <span className="text-gray-700">—</span>
                            )}
                          </td>
                          {/* Committed $ — awaiting approval */}
                          <td className="px-3 py-2.5 text-right font-mono font-semibold">
                            {(row.amountPending || 0) > 0 ? (
                              <span className="text-yellow-300">
                                ${fmt(row.amountPending)}
                              </span>
                            ) : (
                              <span className="text-gray-700">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {isUnbudgeted ? (
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] text-yellow-400 font-mono">
                                  {fmtQty(row.qtyInstalled)} installed
                                </span>
                              </div>
                            ) : (
                              <>
                                <QtyBar
                                  installed={row.qtyInstalled || 0}
                                  pending={row.qtyPending || 0}
                                  netQty={netQty}
                                />
                                {hasCOs && (
                                  <span className="text-[9px] text-gray-600 mt-0.5 block">
                                    B+CO
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {isCONew ? (
                              <span className="text-[9px] bg-purple-700/60 text-purple-100 px-1.5 py-0.5 rounded font-semibold">
                                CO
                              </span>
                            ) : isUnbudgeted ? (
                              <span className="text-[9px] bg-yellow-700/60 text-yellow-100 px-1.5 py-0.5 rounded font-semibold">
                                UNBUD
                              </span>
                            ) : hasCOs ? (
                              <span className="text-[9px] bg-indigo-700/60 text-indigo-100 px-1.5 py-0.5 rounded font-semibold">
                                B+CO
                              </span>
                            ) : (
                              <span className="text-[9px] text-gray-600">
                                Budget
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

                <tfoot className="bg-gray-900 sticky bottom-0 border-t border-gray-600">
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-2.5 text-xs text-gray-400"
                    >
                      {filtered.filter((r) => r.source === "budget").length}{" "}
                      budget items
                      {filtered.filter((r) => r.source === "change_order")
                        .length > 0 &&
                        ` · ${filtered.filter((r) => r.source === "change_order").length} CO new`}
                      {filtered.filter(
                        (r) => r.source === "piecework_unbudgeted",
                      ).length > 0 &&
                        ` · ${filtered.filter((r) => r.source === "piecework_unbudgeted").length} unbudgeted`}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-gray-400 font-mono">
                      {footerTotals.hours.toFixed(1)} hrs
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-blue-300 font-mono">
                      ${fmt(footerTotals.estimated)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-green-300 font-mono">
                      {fmtQty(footerTotals.installed)}
                    </td>
                    <td />
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-orange-300 font-mono">
                      ${fmt(footerTotals.amountActual)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-yellow-300 font-mono">
                      {footerTotals.amountPending > 0
                        ? `$${fmt(footerTotals.amountPending)}`
                        : "—"}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TIMESHEET CHARGES — hourly work orders only
      ══════════════════════════════════════════════════════════════════════ */}
      {isHourly && <TimesheetChargesTable woId={woId} headers={headers} />}

      {/* ══════════════════════════════════════════════════════════════════════
          SUBCONTRACTOR — agreements (create / edit / complete / pay) + bills
      ══════════════════════════════════════════════════════════════════════ */}
      <SubAgreementsPanel
        woId={woId}
        headers={headers}
        defaultUnion={wo.union}
        onChange={refreshWo}
      />

      {isSubcontractor && <BillsChargedTable woId={woId} headers={headers} />}
    </div>
  );
}
